import React from "react";
import useLang from "Shared/hooks/useLanguage";
import PathologyReportSelect from "../PathologyReportSelect";
import {
  grossMacroOptions,
  microMacroOptions,
  finalMacroOptions,
  operationOptions,
  reportMacroOptions,
  siteOptions,
  type ResultPart,
} from "../pathologyReportData";

type Props = {
  parts: ResultPart[];
  activePartId: string;
  onActivePartChange: (id: string) => void;
  onAddPart: () => void;
  onUpdatePart: (id: string, patch: Partial<ResultPart>) => void;
};

const ResultTab = ({
  parts,
  activePartId,
  onActivePartChange,
  onAddPart,
  onUpdatePart,
}: Props) => {
  const { t } = useLang();
  const activePart = parts.find((p) => p.id === activePartId) ?? parts[0];

  if (!activePart) return null;

  const update = (patch: Partial<ResultPart>) =>
    onUpdatePart(activePart.id, patch);

  return (
    <>
      <div className="nav nav-tabs nav-line-tabs nav-line-tabs-2x fs-6 pathologyReportSubTabs mb-4">
        {parts.map((part) => (
          <button
            key={part.id}
            type="button"
            className={`nav-link ${activePartId === part.id ? "active" : ""}`}
            onClick={() => onActivePartChange(part.id)}
            style={
              activePartId === part.id
                ? { borderBottom: "2px solid #69A54B", color: "#69A54B" }
                : { borderBottom: "2px solid transparent" }
            }
          >
            {t(part.label)}
          </button>
        ))}
        <button
          type="button"
          className="nav-link text-gray-600 fw-semibold"
          onClick={onAddPart}
        >
          {t("+ Add New")}
        </button>
      </div>

      <div className="row g-4 mb-4">
        <div className="col-md-4">
          <label className="form-label fw-semibold text-gray-700">{t("Site")}</label>
          <PathologyReportSelect
            value={activePart.site}
            onChange={(site) => update({ site })}
            options={siteOptions}
          />
        </div>
        <div className="col-md-4">
          <label className="form-label fw-semibold text-gray-700">
            {t("Operation")}
          </label>
          <PathologyReportSelect
            value={activePart.operation}
            onChange={(operation) => update({ operation })}
            options={operationOptions}
          />
        </div>
        <div className="col-md-4">
          <label className="form-label fw-semibold text-gray-700">
            {t("Clinical data")}
          </label>
          <input
            type="text"
            className="form-control h-30px rounded-2"
            placeholder={t("e.g. Cecal neoplasm / Malignant neoplasm of up...")}
            value={activePart.clinicalData}
            onChange={(e) => update({ clinicalData: e.target.value })}
          />
        </div>
      </div>

      <div className="mb-4">
        <label className="form-label fw-semibold text-gray-700">
          {t("Diagnosis")}
        </label>
        <textarea
          className="form-control rounded-2 pathology-report-textarea-sm"
          value={activePart.diagnosis}
          onChange={(e) => update({ diagnosis: e.target.value })}
        />
      </div>

      <div className="row g-4">
        <div className="col-lg-6">
          <div className="border rounded-3 overflow-hidden h-100">
            <div className="bg-light px-4 py-2 fw-semibold text-gray-700">
              {t("Gross description")}
            </div>
            <div className="p-4">
              <PathologyReportSelect
                value={activePart.grossMacro}
                onChange={(grossMacro) => update({ grossMacro })}
                options={grossMacroOptions}
              />
              <textarea
                className="form-control rounded-2 mt-3 pathology-report-textarea"
                value={activePart.grossDescription}
                onChange={(e) => update({ grossDescription: e.target.value })}
              />
            </div>
          </div>
        </div>
        <div className="col-lg-6">
          <div className="border rounded-3 overflow-hidden h-100">
            <div className="bg-light px-4 py-2 fw-semibold text-gray-700">
              {t("Microscopic description")}
            </div>
            <div className="p-4">
              <PathologyReportSelect
                value={activePart.microMacro}
                onChange={(microMacro) => update({ microMacro })}
                options={microMacroOptions}
              />
              <textarea
                className="form-control rounded-2 mt-3 pathology-report-textarea"
                value={activePart.microDescription}
                onChange={(e) => update({ microDescription: e.target.value })}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="row g-4 mt-2">
        <div className="col-lg-6">
          <div className="border rounded-3 overflow-hidden h-100">
            <div className="bg-light px-4 py-2 fw-semibold text-gray-700">
              {t("Final description")}
            </div>
            <div className="p-4">
              <PathologyReportSelect
                value={activePart.finalMacro}
                onChange={(finalMacro) => update({ finalMacro })}
                options={finalMacroOptions}
                placeholder="-- Select macro --"
              />
              <textarea
                className="form-control rounded-2 mt-3 pathology-report-textarea"
                placeholder={t("Description...")}
                value={activePart.finalDescription}
                onChange={(e) => update({ finalDescription: e.target.value })}
              />
            </div>
          </div>
        </div>
        <div className="col-lg-6">
          <div className="border rounded-3 overflow-hidden h-100">
            <div className="bg-light px-4 py-2 fw-semibold text-gray-700">
              {t("Report description")}
            </div>
            <div className="p-4">
              <PathologyReportSelect
                value={activePart.reportMacro}
                onChange={(reportMacro) => update({ reportMacro })}
                options={reportMacroOptions}
                placeholder="-- Select macro --"
              />
              <textarea
                className="form-control rounded-2 mt-3 pathology-report-textarea"
                placeholder={t("Description...")}
                value={activePart.reportDescription}
                onChange={(e) => update({ reportDescription: e.target.value })}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4">
        <label className="form-label fw-semibold text-gray-700">
          {t("Comment (optional)")}
        </label>
        <textarea
          className="form-control rounded-2 pathology-report-textarea-sm"
          placeholder={t("Pathologist comment...")}
          value={activePart.comment}
          onChange={(e) => update({ comment: e.target.value })}
        />
      </div>
    </>
  );
};

export default ResultTab;
